import {
  BattingCageCookie,
  InternalSessionIdCookie,
  UserIPCookie,
  api,
  cloneDeep,
  getCurrentURL,
  getTriggersByUrlOnObject,
  groupArrayOfObjByKey,
  isNumber,
  logger,
  store,
} from "../shared";
import {
  IBehaviorType,
  IContentResponse,
  IEventLoggerEnum,
  IPersistedExperience,
  ITrigger,
  ITriggerResponse,
  ITriggersByPageUrl,
  TriggerPageUrl,
} from "../types";
import { EventLoggerModule } from "./event-logger";
import { TriggerEventsModule } from "./trigger-events";
import { TriggerPreviewsModule } from "./trigger-previews";

const contentEndpoint = process.env.CONTENT_API;
const triggerEndpoint = process.env.TRIGGER_API;

export const TriggersModule = (() => {
  const isProduction = process.env.NODE_ENV === "production";

  const getTriggers = async () => {
    const isPreviewMode = store.get("isPreviewMode");

    let contentData: IContentResponse | undefined;
    let triggerData: ITriggerResponse | undefined;

    let internalSessionId: number | undefined;

    const ownedAndOperatedInternalSessionId = InternalSessionIdCookie.get();

    const hasOwnedAndOperatedInternalSessionId = isNumber(
      ownedAndOperatedInternalSessionId
    );

    const userIP = UserIPCookie.get();

    if (isProduction) {
      if (hasOwnedAndOperatedInternalSessionId)
        internalSessionId = parseInt(
          ownedAndOperatedInternalSessionId as string
        );
    } else {
      try {
        const contentResponse = await api.get({
          url: `${contentEndpoint}?localtest=true&client_ip=${userIP}`,
          headers: {
            "Page-Url": "http://localhost:6075/internet?localtest=true",
          },
        });

        contentData = await contentResponse.json();
        internalSessionId = contentData?.internalSessionId!;
      } catch (error) {
        logger.error({
          message: "Failed to fetch content endpoint",
          error,
        });
      }
    }

    const hasInternalSessionId = isNumber(internalSessionId);

    if (hasInternalSessionId || isPreviewMode) {
      try {
        const userAgent = navigator.userAgent;

        let internalIdString = "";

        if (!isPreviewMode && internalSessionId) {
          internalIdString = `internal_session_id=${internalSessionId}`;
        }

        let triggerUrl = `${triggerEndpoint}/?${internalIdString}&User-Agent=${userAgent}&client_ip=${
          userIP || ""
        }`;

        const previewModeParameters = store.get("previewModeParameters");

        if (isPreviewMode) {
          const { previewClusterVersionId, previewExperimentVariationId } =
            previewModeParameters;
          if (previewClusterVersionId) {
            triggerUrl += `&preview_cluster_version=${previewClusterVersionId}`;
          }
          if (previewExperimentVariationId) {
            triggerUrl += `&preview_experiment_variation=${previewExperimentVariationId}`;
          }
        }

        const triggerResponse = await api.get({
          url: triggerUrl,
          headers: {
            "Page-Url": getCurrentURL(),
          },
        });

        triggerData = await triggerResponse.json();
      } catch (error) {
        EventLoggerModule.logEvent({
          eventType: IEventLoggerEnum.GenericError,
          message: "Failed to fetch bating cage script trigger endpoint",
          payload: {
            error,
          },
        });
      }
    }

    if (internalSessionId) {
      store.set("internalSessionId", internalSessionId);
    }

    if (triggerData) {
      store.set("triggersByPage", triggerData?.triggersByPage);
      store.set("universalTriggers", triggerData?.universalTriggers);
    }

    return triggerData;
  };

  const getTriggersByCurrentPageUrl = (
    triggersByPage: ITriggersByPageUrl | undefined
  ) => {
    if (!triggersByPage) return [];

    const triggersOnCurrentPage = getTriggersByUrlOnObject(
      triggersByPage
    ) as unknown as ITrigger[];

    return triggersOnCurrentPage;
  };

  const verifySessionIdCookeHasNotChangedOnPageVisibilityShow = (
    isPageHidden: boolean
  ) => {
    if (isPageHidden || store.get("isPreviewMode")) return;

    const battingCageCookie = BattingCageCookie.get();
    const internalSessionIdFromStore = store.get("internalSessionId");

    const internalSessionIdFromCookie =
      battingCageCookie?.meta?.internalSessionId;

    const hasInternalSessionIdFromStore = isNumber(internalSessionIdFromStore);
    const hasInternalSessionIdFromCookie = isNumber(
      internalSessionIdFromCookie
    );

    let internalSessionIdHasChanged = false;

    if (hasInternalSessionIdFromStore && hasInternalSessionIdFromCookie) {
      internalSessionIdHasChanged =
        internalSessionIdFromStore !== internalSessionIdFromCookie;
    }

    if (internalSessionIdHasChanged) {
      const updatedCookie = {
        meta: {
          internalSessionId: internalSessionIdFromCookie,
        },
        persistedExperience: [],
      };

      BattingCageCookie.set({ value: updatedCookie });
      store.set("persistedExperience", []);
      store.set("internalSessionId", internalSessionIdFromCookie);
    }
  };

  const getAppliedTriggersFromCookie = () => {
    if (store.get("isPreviewMode")) {
      const persistedPreviewExperience =
        TriggerPreviewsModule.getPersistedExperienceFromBattingCagePreviewCookie();

      store.set("persistedExperience", persistedPreviewExperience || []);
      return [];
    }

    const battingCageCookie = BattingCageCookie.get();

    const internalSessionIdFromContentResponse = store.get("internalSessionId");

    const internalSessionIdFromCookie =
      battingCageCookie?.meta?.internalSessionId;

    const hasInternalSessionIdFromContentResponse = isNumber(
      internalSessionIdFromContentResponse
    );
    const hasInternalSessionIdFromCookie = isNumber(
      internalSessionIdFromCookie
    );

    const internalSessionIdFromCookieDoesNotExist =
      !hasInternalSessionIdFromCookie;

    // First time setting cookie or cookie has been cleared
    if (
      internalSessionIdFromCookieDoesNotExist &&
      hasInternalSessionIdFromContentResponse
    ) {
      const updatedCookie = {
        meta: {
          internalSessionId: internalSessionIdFromContentResponse,
        },
        persistedExperience: [],
      };

      BattingCageCookie.set({ value: updatedCookie });
      store.set("persistedExperience", []);
      return [];
    }

    // Session ID has changed
    if (
      hasInternalSessionIdFromContentResponse &&
      hasInternalSessionIdFromCookie
    ) {
      const hasInternalSessonIdChanged =
        internalSessionIdFromContentResponse !== internalSessionIdFromCookie;

      if (hasInternalSessonIdChanged) {
        const updatedCookie = {
          meta: {
            internalSessionId: internalSessionIdFromContentResponse,
          },
          persistedExperience: [],
        };

        BattingCageCookie.set({ value: updatedCookie });
        store.set("persistedExperience", []);

        return [];
      }
    }

    const persistedExperienceFromCookie =
      battingCageCookie?.persistedExperience as IPersistedExperience[];

    const ONE_DAY = new Date().getTime() + 24 * 60 * 60 * 1000;

    /*
      Remove all IBehaviorType except Click that are older than one day    
    */
    const filteredPersistedExperienceFromCookie =
      persistedExperienceFromCookie.filter((persistedExperience) => {
        const { behaviorType, utcTimestamp } = persistedExperience;
        if (behaviorType === IBehaviorType.Click) return true;

        const oneDayAfterUTCTimeStamp =
          new Date(utcTimestamp).getTime() + ONE_DAY;

        const nowUTC = new Date(new Date().toUTCString()).getTime();

        return nowUTC < oneDayAfterUTCTimeStamp;
      });

    store.set("persistedExperience", filteredPersistedExperienceFromCookie);

    BattingCageCookie.set({
      value: {
        meta: {
          internalSessionId: internalSessionIdFromContentResponse,
        },
        persistedExperience: filteredPersistedExperienceFromCookie,
      },
    });

    return persistedExperienceFromCookie;
  };

  const getTriggerEventsToApplyOnCurrentPage = ({
    pageTriggers,
    appliedTriggers,
  }: {
    pageTriggers: ITrigger[];
    appliedTriggers: IPersistedExperience[];
  }) => {
    const universalTriggers = store.get("universalTriggers");

    const allTriggers = [...(universalTriggers || []), ...(pageTriggers || [])];

    const clonedTriggers = cloneDeep(allTriggers);
    const triggersGroupedByBehaviorId = groupArrayOfObjByKey(
      clonedTriggers,
      "behaviorId"
    ) as unknown as Record<string, ITrigger>;

    if (appliedTriggers.length) {
      appliedTriggers.forEach((appliedTrigger) => {
        if (triggersGroupedByBehaviorId[appliedTrigger.behaviorId]) {
          delete triggersGroupedByBehaviorId[appliedTrigger.behaviorId];
        }
      });
    }
    return Object.values(triggersGroupedByBehaviorId).flat();
  };

  // const getTriggerEventsToApplyOnCurrentPage = ({
  //   allTriggersOnCurrentPage,
  //   appliedTriggersOnCurrentPage,
  // }: {
  //   allTriggersOnCurrentPage: ITrigger[];
  //   appliedTriggersOnCurrentPage: IPersistedExperience[];
  // }) => {
  //   const clonedTriggers = cloneDeep(allTriggersOnCurrentPage);
  //   const triggersGroupedByBehaviorId = groupArrayOfObjByKey(
  //     clonedTriggers,
  //     "behaviorId"
  //   ) as unknown as Record<string, ITrigger>;

  //   if (appliedTriggersOnCurrentPage.length) {
  //     appliedTriggersOnCurrentPage.forEach((appliedTrigger) => {
  //       if (triggersGroupedByBehaviorId[appliedTrigger.behaviorId]) {
  //         delete triggersGroupedByBehaviorId[appliedTrigger.behaviorId];
  //       }
  //     });
  //   }
  //   return Object.values(triggersGroupedByBehaviorId).flat();
  // };

  const getTriggersOnCurrentPage = async () => {
    const triggersByPage =
      store.get("triggersByPage") || (await getTriggers())?.triggersByPage;

    if (triggersByPage) {
      const allTriggersOnCurrentPage =
        getTriggersByCurrentPageUrl(triggersByPage);

      const appliedTriggers = getAppliedTriggersFromCookie();

      const triggerEventsToApplyOnCurrentPage =
        getTriggerEventsToApplyOnCurrentPage({
          pageTriggers: allTriggersOnCurrentPage,
          appliedTriggers,
        });

      store.set("currentPageTriggers", triggerEventsToApplyOnCurrentPage);

      return triggerEventsToApplyOnCurrentPage;
    }
  };

  const setTriggers = async () => {
    await getTriggersOnCurrentPage();

    await TriggerEventsModule.setTriggerEventListenersOnCurrentPage();
  };

  return {
    start: async () => {
      setTriggers();
    },
    getTriggersOnCurrentPage,
    verifySessionIdCookeHasNotChangedOnPageVisibilityShow,
  };
})();

/**
 *
 * Save Persisted Experience Triggers to a preview cookie, use same schema as
 * batting-cage-script-cookie to keep it easy
 *
 * Send to Artem and Bilal once I have the schema complete
 *
 * Preview triggers universal zones
 */
